import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import type { SupplierVendorNexAliasGroup } from '../../hooks/useGetSettings';
import { SupplierSearchField } from '../../components/SupplierSearchField';
import { parseAliasListInput } from '../../utils/vendorNexAliasMappingsUtils';

interface AddSupplierVendorMappingModalProps {
    isOpen: boolean;
    onClose: () => void;
    excludedSupplierIds: string[];
    onAdd: (group: SupplierVendorNexAliasGroup) => void;
    isDisabled?: boolean;
}

export default function AddSupplierVendorMappingModal({
    isOpen,
    onClose,
    excludedSupplierIds,
    onAdd,
    isDisabled,
}: AddSupplierVendorMappingModalProps) {
    const [selectedSupplier, setSelectedSupplier] = useState<{ id: string; title: string } | null>(
        null
    );
    const [invoiceTextInput, setInvoiceTextInput] = useState('');
    const [nexVendorNamesInput, setNexVendorNamesInput] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setSelectedSupplier(null);
            setInvoiceTextInput('');
            setNexVendorNamesInput('');
        }
    }, [isOpen]);

    const invoiceAliases = parseAliasListInput(invoiceTextInput);
    const nexNames = parseAliasListInput(nexVendorNamesInput);

    const canSubmit =
        Boolean(selectedSupplier) &&
        (invoiceAliases.length > 0 || nexNames.length > 0) &&
        !isDisabled;

    const handleClose = () => {
        onClose();
    };

    const handleSubmit = () => {
        if (!selectedSupplier || !canSubmit) return;
        onAdd({
            supplierId: selectedSupplier.id,
            supplierTitle: selectedSupplier.title,
            invoiceTextAliases: invoiceAliases,
            nexVendorMappings: nexNames.map((vendorName) => ({ vendorName })),
        });
        handleClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader fontSize="md" fontWeight="semibold" color="gray.800" py={4}>
                    Add Supplier
                </ModalHeader>
                <ModalCloseButton top={3} />
                <ModalBody>
                    <VStack align="stretch" spacing={4}>
                        <Text fontSize="sm" color="gray.600">
                            Pick a Yaxle supplier and add invoice wording and Nexsyis PO vendor names.
                            Separate multiple values with commas.
                        </Text>

                        <FormControl isRequired>
                            <FormLabel fontSize="sm">Supplier</FormLabel>
                            {selectedSupplier ? (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap={2}
                                    p={2}
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                    bg="gray.50"
                                >
                                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                        {selectedSupplier.title}
                                    </Text>
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        onClick={() => setSelectedSupplier(null)}
                                    >
                                        Change
                                    </Button>
                                </Box>
                            ) : (
                                <SupplierSearchField
                                    placeholder="Search suppliers…"
                                    emptyMessage="No suppliers found."
                                    excludeIds={excludedSupplierIds}
                                    onSelect={(supplier) => {
                                        setSelectedSupplier({
                                            id: supplier._id,
                                            title: supplier.title,
                                        });
                                    }}
                                />
                            )}
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm">Text on invoice</FormLabel>
                            <Input
                                size="sm"
                                value={invoiceTextInput}
                                onChange={(e) => setInvoiceTextInput(e.target.value)}
                                placeholder="e.g. Brock Supply, ORW USA INC"
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Comma-separated invoice vendor wording
                            </Text>
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm">Nexsyis vendor names</FormLabel>
                            <Input
                                size="sm"
                                value={nexVendorNamesInput}
                                onChange={(e) => setNexVendorNamesInput(e.target.value)}
                                placeholder="e.g. Brock Auto Sales, 4 Wheel Auto Parts"
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Comma-separated names as shown on Nexsyis POs
                            </Text>
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button size="action" variant="outlineGray" mr={3} onClick={handleClose} isDisabled={isDisabled}>
                        Cancel
                    </Button>
                    <Button
                        size="action"
                        colorScheme="partbay-blue"
                        onClick={handleSubmit}
                        isDisabled={!canSubmit}
                        isLoading={isDisabled}
                    >
                        Submit
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
